import React from "react";
import { motion, AnimatePresence } from "framer-motion";
import { AlertCircle, AlertTriangle, Info, X } from "lucide-react";
import { Button } from "@/components/ui/button";

interface ConfirmationOptions {
  title: string;
  description: string;
  confirmLabel?: string;
  cancelLabel?: string;
  variant?: "danger" | "primary" | "warning";
}

interface ConfirmationModalProps {
  isOpen: boolean;
  options: ConfirmationOptions;
  onConfirm: () => void;
  onCancel: () => void;
}

export const ConfirmationModal = ({ isOpen, options, onConfirm, onCancel }: ConfirmationModalProps) => {
  const { 
    title, 
    description, 
    confirmLabel = "Confirm", 
    cancelLabel = "Cancel", 
    variant = "primary" 
  } = options;

  const variants = {
    danger: {
      icon: <AlertCircle className="w-6 h-6 text-red-600" />,
      bg: "bg-red-50",
      button: "bg-red-600 hover:bg-red-700 text-white shadow-lg shadow-red-200",
      border: "border-red-100"
    },
    warning: {
      icon: <AlertTriangle className="w-6 h-6 text-amber-600" />,
      bg: "bg-amber-50",
      button: "bg-amber-600 hover:bg-amber-700 text-white shadow-lg shadow-amber-200",
      border: "border-amber-100"
    },
    primary: {
      icon: <Info className="w-6 h-6 text-blue-600" />,
      bg: "bg-blue-50",
      button: "bg-primary hover:bg-primary/90 text-white shadow-lg shadow-primary/20",
      border: "border-blue-100"
    }
  };

  const style = variants[variant];

  return (
    <AnimatePresence>
      {isOpen && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 sm:p-6">
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onCancel}
            className="absolute inset-0 bg-slate-900/40 backdrop-blur-sm"
          />

          {/* Modal */}
          <motion.div
            initial={{ opacity: 0, scale: 0.95, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: 20 }}
            className="relative w-full max-w-md bg-white rounded-2xl shadow-2xl overflow-hidden border border-slate-100"
          >
            <div className="p-6">
              <div className="flex items-start gap-4">
                <div className={`flex-shrink-0 w-12 h-12 rounded-full flex items-center justify-center ${style.bg}`}>
                  {style.icon}
                </div>
                <div className="flex-1 pt-1">
                  <h3 className="text-xl font-bold text-slate-900 leading-tight">
                    {title}
                  </h3>
                  <p className="mt-3 text-slate-500 leading-relaxed text-sm">
                    {description}
                  </p>
                </div>
                {/* Close Button */}
                <button 
                  onClick={onCancel}
                  className="p-1 rounded-lg hover:bg-slate-100 text-slate-400 transition-colors"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>

              <div className="mt-8 flex flex-col-reverse sm:flex-row sm:justify-end gap-3">
                <Button
                  variant="ghost"
                  onClick={onCancel}
                  className="w-full sm:w-auto h-11 rounded-xl text-slate-500 hover:text-slate-700 hover:bg-slate-50 font-medium"
                >
                  {cancelLabel}
                </Button>
                <Button
                  onClick={onConfirm}
                  className={`w-full sm:w-auto h-11 px-8 rounded-xl font-semibold transition-all active:scale-95 ${style.button}`}
                >
                  {confirmLabel}
                </Button>
              </div>
            </div>

            {/* Subtle Gradient bar for accent */}
            <div className={`h-1.5 w-full bg-gradient-to-r ${variant === 'danger' ? 'from-red-500 to-rose-400' : 'from-primary to-blue-400'}`} />
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );
};
